// Bookmark dropdown in the map's top overlay: the quick-nav path to saved
// places. Picking an entry flies the main map to it; managing the list lives
// on /saved. Anonymous users get bounced to /login on click, same as the
// other inline-gated controls.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Bookmark, MapPin } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { gqlRequest } from "../lib/gql";
import { MY_SAVED_PLACES, type MySavedPlacesResult, type SavedPlaceEntry } from "../lib/queries";
import { PLACE_TYPE_ICONS } from "../lib/format";
import { pointCoords } from "../lib/geo";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import type { Camera } from "../map/MapView";

/** Zoom used when jumping to a saved entry — well past the pin floor. */
const SAVED_FLY_ZOOM = 14;

export function savedEntryCoords(entry: SavedPlaceEntry): [number, number] | null {
  return pointCoords(entry.geom ?? entry.place?.geom);
}

export function SavedPlacesMenu({ onFlyTo }: { onFlyTo: (camera: Camera) => void }) {
  const { personId } = useAuth();
  const { t, entityName } = useT();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["saved", personId],
    enabled: personId != null && open,
    queryFn: () => gqlRequest<MySavedPlacesResult>(MY_SAVED_PLACES, { personId }),
  });
  const entries = data?.savedPlaces.nodes ?? [];

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (next && !personId) {
          navigate(`/login?next=${encodeURIComponent("/")}`);
          return;
        }
        setOpen(next);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={t("map.savedPlaces")}
          className={cn(
            "flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium shadow-card transition-all duration-150 active:scale-[0.97]",
            open
              ? "border-primary/25 bg-primary/5 text-primary"
              : "border-border bg-background text-foreground hover:border-muted-foreground/40 hover:shadow-card-hover",
          )}
        >
          <Bookmark aria-hidden className="size-4" />
          <span className="hidden sm:inline">{t("map.savedPlaces")}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-40 w-64 rounded-xl shadow-pop">
        {isLoading && <p className="px-3.5 py-2 text-sm text-muted-foreground">{t("common.loading")}</p>}
        {!isLoading && entries.length === 0 && (
          <p className="px-3.5 py-2 text-sm text-muted-foreground">{t("map.savedEmpty")}</p>
        )}
        <div className="max-h-72 overflow-y-auto">
          {entries.map((entry) => {
            const coords = savedEntryCoords(entry);
            if (!coords) return null;
            const Icon = entry.place ? (PLACE_TYPE_ICONS[entry.place.type] ?? MapPin) : MapPin;
            return (
              <DropdownMenuItem
                key={entry.id}
                onSelect={() => onFlyTo({ lng: coords[0], lat: coords[1], zoom: SAVED_FLY_ZOOM })}
                className="gap-2.5 px-3.5 py-2 font-medium"
              >
                <Icon aria-hidden className="size-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate">
                  {entry.place ? entityName(entry.place) : entry.name}
                </span>
                {entry.place?.elevation != null && (
                  <span className="shrink-0 text-xs text-muted-foreground">{entry.place.elevation} m</span>
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
        {entries.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild className="px-3.5 py-2 font-medium text-primary">
              <Link href="/saved">{t("map.manageSaved")}</Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
